import type { MaterialEntry } from "./DailyReportPage";

interface Props {
  materials: MaterialEntry[];
  onChange: (index: number, qty: string) => void;
  // True when the operator is editing an earlier day. Stock-in entries are
  // point-in-time deliveries — they're never pre-filled. The cue avoids the
  // "blank fields look like lost data" confusion.
  editMode?: boolean;
}

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

export default function StepRawMaterials({ materials, onChange, editMode }: Props) {
  return (
    <div>
      <h4 className="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">
        Raw Material Received (Stock In)
      </h4>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        Enter quantities for materials delivered on site today. Leave blank if none.
      </p>
      {editMode && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-400">
          Editing an earlier day: any stock-in entries already saved for this
          date stay as-is. Leave fields blank to skip, or enter a value to
          record an ADDITIONAL delivery on top of what's there.
        </div>
      )}

      {materials.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No raw materials configured. Add them in Master Data.
        </p>
      ) : (
        <div className="space-y-3">
          {materials.map((m, i) => (
            <div key={m.raw_material_id} className="flex items-center gap-3">
              <label className="w-40 shrink-0 text-sm font-medium text-gray-700 dark:text-gray-300">
                {m.name}
                <span className="ml-1 text-xs text-gray-400">({m.unit})</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={m.qty}
                onChange={(e) => onChange(i, e.target.value)}
                className={inputClass}
                placeholder="0"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
